/**
 * The clock's commands (wave C, decision 0071, spec §5, §7; decisions C2, C3). People set, pause, resume and remove
 * monitors (members, not viewers; pause and remove: the setter, the agent's keeper or an admin). `system:scheduler`
 * fires them, notes the daily check's loose ends and notes a recurring series that came due. None of these creates an
 * item, a card a person did not cause, or work: the timed turns they start are visible on the agent's History, asked by
 * the person who set the monitor (the series' Asked by; nobody for the daily check).
 */
import { autonomyOf } from "../autonomy"
import { deriveId } from "../hash"
import { looseEnds } from "../loose-ends"
import {
  isFireTime,
  monitorOf,
  projectDay,
  seriesDueProblem,
  setterGone,
  timedCapReached,
  triggerProblem,
} from "../monitors"
import { isWorkingMember } from "../multiplayer"
import { capabilityVerdict } from "../policy"
import { periodOf } from "../recurrence"
import { agentOf, isProjectAdmin, isRunEnded, itemOf, runOf } from "../state"
import { QUIET_RUN_MS } from "../turn-ends"
import type { Command, Ids, Monitor, ProjectState } from "../types"
import { checkSetMonitor } from "../wakes"
import { holdTimed } from "./items"
import { forbidden, invalid, type Tx } from "./tx"

type Cmd<T extends Command["_tag"]> = Extract<Command, { _tag: T }>

const HHMM = /^(?:[01]\d|2[0-3]):[0-5]\d$/

const requireMember = (tx: Tx): Ids["UserId"] => {
  const a = tx.actor
  if (a._tag !== "user")
    forbidden("actor_kind", "people set monitors; an agent proposes one (propose_monitor)")
  if (!isWorkingMember(tx.state, a.userId))
    forbidden("not_owner", "only a project member (not a viewer) sets monitors")
  return a.userId
}

/** A persistent agent schedules its own work using the current run's principal, never an input's payer. */
const writer = (tx: Tx, agentId: Ids["AgentId"], starts = true): Ids["UserId"] => {
  if (tx.actor._tag === "user") return requireMember(tx)
  const actor = tx.actor
  if (actor._tag !== "agent" || actor.agentId !== agentId)
    forbidden("actor_kind", "a persistent agent manages its own monitors")
  const agent = agentOf(tx.state, agentId)
  const run = runOf(tx.state, actor.runId)
  if (!agent || run?.kind !== "turn" || run.agentId !== agentId || isRunEnded(run))
    forbidden("actor_kind", "manage a monitor from this agent's turn")
  if (!isWorkingMember(tx.state, run.ownerId))
    forbidden("not_owner", "the turn's principal is no longer a project member")
  if (capabilityVerdict(tx.state, agent, "schedule_work") !== "allow")
    forbidden("policy_never", "schedule_work requires approval under this policy; use propose_monitor")
  if (starts) {
    const level = autonomyOf(tx.state, actor)
    if (level !== null && level !== "act")
      forbidden("autonomy", "propose_monitor asks a person before starting scheduled work")
  }
  return run.ownerId
}

const requireMonitor = (s: ProjectState, id: Ids["MonitorId"]): Monitor =>
  monitorOf(s, id) ?? invalid("no such monitor", "monitorId")

/** The setter, the agent's keeper or a workspace owner/admin. */
const requireSteer = (tx: Tx, m: Monitor, what: string): void => {
  const u = writer(tx, m.agentId, what === "resume" || what === "change")
  if (tx.actor._tag === "agent") {
    if (m.ownerId !== u)
      forbidden("not_owner", "this monitor belongs to another principal; its setter can change it")
    return
  }
  const keeper = agentOf(tx.state, m.agentId)?.ownerId
  if (u !== m.ownerId && u !== keeper && !isProjectAdmin(tx.state, u))
    forbidden("not_owner", `only the person who set it, the agent's keeper or an admin may ${what} a monitor`)
}

/**
 * Check a monitor as it would be set by `setter` and stamp what decide owns (setter, state, lineage, the kept
 * `lastFiredAt`). Shared with Accept on an agent's `monitor` card (ledger). Throws Invalid on a problem.
 */
export const monitorToSet = (
  s: ProjectState,
  m: Monitor,
  setter: Ids["UserId"],
  path = "monitor",
): Monitor => {
  const agent = agentOf(s, m.agentId)
  if (!agent || agent.status === "retired") invalid("no such agent to wake", `${path}.agentId`)
  if (s.mayor?.agentId === m.agentId) invalid("the Mayor has no monitors in a project", `${path}.agentId`)
  if (m.owner._tag === "agent" && m.owner.agentId !== m.agentId)
    invalid("a monitor wakes the agent it belongs to", `${path}.owner`)
  if (m.owner._tag === "area" && (m.owner.areaId !== agent.areaId || m.owner.projectId !== s.project?.id))
    invalid("an area's monitor wakes that area's agent", `${path}.owner`)
  const problem = triggerProblem(m.trigger)
  if (problem) invalid(problem, `${path}.trigger`)
  const q = m.quietHours
  if (q !== null && (!HHMM.test(q.from) || !HHMM.test(q.to)))
    invalid("quiet hours are HH:MM to HH:MM", `${path}.quietHours`)
  const instruction = m.instruction.trim()
  if (instruction.length === 0 || instruction.length > 600)
    invalid("say in one line what the agent should do (1-600 characters)", `${path}.instruction`)
  const lineage = checkSetMonitor(m.kind, null)
  if (lineage._tag === "rejected") invalid(lineage.why, `${path}.kind`)
  const existing = monitorOf(s, m.id)
  return {
    ...m,
    instruction,
    ownerId: setter,
    createdBy: existing?.createdBy ?? { _tag: "user", userId: setter },
    visibility: "workspace",
    projectId: s.project?.id ?? null,
    ...(q !== null ? { quietHours: { from: q.from, to: q.to, tz: q.tz } } : {}),
    state: { _tag: "active" },
    createdByRoutine: lineage.createdByRoutine,
    lastFiredAt: existing?.lastFiredAt ?? null,
  }
}

/**
 * SetMonitor: a person sets a monitor (new, or an edit replacing it whole). Members, not viewers. An edit keeps who set
 * it (its turns stay asked by, and cost, that person), whether it is paused and whether it is blocked: only a new
 * monitor or Resume as me makes someone its setter, and only Pause / Resume turn it off or on.
 */
export const setMonitor = (tx: Tx, c: Cmd<"SetMonitor">): void => {
  const u = writer(tx, c.monitor.agentId)
  const existing = monitorOf(tx.state, c.monitor.id)
  if (existing) requireSteer(tx, existing, "change")
  const set = monitorToSet(tx.state, c.monitor, existing?.ownerId ?? u)
  const routine =
    tx.actor._tag === "agent"
      ? runOf(tx.state, tx.actor.runId)?.wakeReasons?.find(
          (r) => r._tag === "monitor" && r.kind === "routine",
        )
      : undefined
  tx.emit({
    _tag: "MonitorSet",
    monitor: existing
      ? { ...set, state: existing.state, enabled: existing.enabled, pausedBy: existing.pausedBy ?? null }
      : {
          ...set,
          createdBy: tx.actor,
          pausedBy: null,
          createdByRoutine: routine?._tag === "monitor" ? routine.monitorId : null,
        },
  })
}

export const removeMonitor = (tx: Tx, c: Cmd<"RemoveMonitor">): void => {
  const m = requireMonitor(tx.state, c.monitorId)
  requireSteer(tx, m, "remove")
  tx.emit({ _tag: "MonitorRemoved", monitorId: m.id })
}

export const setMonitorPaused = (tx: Tx, c: Cmd<"SetMonitorPaused">): void => {
  const m = requireMonitor(tx.state, c.monitorId)
  requireSteer(tx, m, c.paused ? "pause" : "resume")
  if (
    !c.paused &&
    !m.enabled &&
    tx.actor._tag === "agent" &&
    (m.pausedBy === undefined || m.pausedBy?._tag === "user") &&
    autonomyOf(tx.state, tx.actor) !== null
  )
    forbidden("not_owner", "a person paused this monitor; they must ask to resume it")
  if (m.enabled === !c.paused) invalid(`the monitor is already ${c.paused ? "paused" : "on"}`, "paused")
  tx.emit({ _tag: "MonitorPaused", monitorId: m.id, paused: c.paused })
}

/** Resume as me: a blocked monitor (its setter left, or a turn failed) fires again, set by the person who resumes it. */
export const resumeMonitor = (tx: Tx, c: Cmd<"ResumeMonitor">): void => {
  const m = requireMonitor(tx.state, c.monitorId)
  const u = writer(tx, m.agentId)
  if (tx.actor._tag === "agent") requireSteer(tx, m, "resume")
  if (m.state._tag !== "blocked" && setterGone(tx.state, m) === null)
    invalid("the monitor is not blocked", "monitorId")
  tx.emit({ _tag: "MonitorStateChanged", monitorId: m.id, state: { _tag: "active" }, ownerId: u })
}

/**
 * system:scheduler, the clock reached a monitor's fire time `at` (core `dueMonitors`). The setter must still be a
 * working member: otherwise the monitor is blocked ("Becky left the project") and nobody wakes. Past the agent's daily
 * cap the wake waits on the day's `timed:` card. Reactions wake the agent with `monitor{monitorId, kind}`.
 */
export const fireMonitor = (tx: Tx, c: Cmd<"FireMonitor">): void => {
  const s = tx.state
  const m = requireMonitor(s, c.monitorId)
  if (m.state._tag !== "active" || !m.enabled) invalid("the monitor is not on", "monitorId")
  const agent = agentOf(s, m.agentId)
  if (agent?.status !== "active") invalid("the monitor's agent is not active", "monitorId")
  if (!isFireTime(m.trigger, m.quietHours, c.at)) invalid("not one of the monitor's fire times", "at")
  if (m.lastFiredAt !== null && c.at < m.lastFiredAt + 60_000)
    invalid("this monitor fire has already been recorded", "at")
  const gone = setterGone(s, m)
  if (gone !== null) {
    tx.emit({
      _tag: "MonitorStateChanged",
      monitorId: m.id,
      state: { _tag: "blocked", reason: gone, runId: null },
    })
    return
  }
  const day = projectDay(s, c.at)
  const held = timedCapReached(s, agent, day)
  tx.emit({ _tag: "MonitorFired", monitorId: m.id, at: c.at, day, ...(held ? { held: true as const } : {}) })
  if (held)
    holdTimed(tx, {
      proposalId: deriveId<Ids["ProposalId"]>("prop", tx.now, `timed:${agent.id}:${day}`),
      agent,
      reason: { _tag: "monitor", monitorId: m.id, kind: m.kind },
      cause: {
        log: { _tag: "project", projectId: s.project?.id as Ids["ProjectId"] },
        seq: tx.state.seq as Ids["Seq"],
      },
      day,
    })
}

/**
 * system:scheduler, the daily check (A1): `keys` must be the loose ends open at `at` (core `looseEnds`) and differ from
 * the last check's. Reactions wake the owners of the new keys with `loose_ends`; a shrunk set wakes nobody.
 */
export const noteLooseEnds = (tx: Tx, c: Cmd<"NoteLooseEnds">): void => {
  const current = looseEnds(tx.state, c.at).map((l) => l.key)
  const same = (a: ReadonlyArray<string>, b: ReadonlyArray<string>) =>
    a.length === b.length && a.every((k, i) => k === b[i])
  if (!same(current, c.keys)) invalid("these are not the loose ends open at that time", "keys")
  const prev = [...(tx.state.dailyCheck?.keys ?? [])].sort()
  if (same([...c.keys].sort(), prev)) invalid("nothing changed since the last check", "keys")
  tx.emit({ _tag: "LooseEndsNoted", at: c.at, keys: c.keys })
}

/**
 * system:scheduler: a recurring series is due for `period` with no open occurrence, not paused or closed, owned by an
 * active agent. Its owner agent wakes with `series_due` (reactions), asked by the series' Asked by; past the agent's
 * daily cap the wake waits on the day's `timed:` card. The system never creates the occurrence: the agent presses Run
 * now.
 */
export const noteSeriesDue = (tx: Tx, c: Cmd<"NoteSeriesDue">): void => {
  const s = tx.state
  const t = itemOf(s, c.itemId)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(c.period)) invalid("a period is yyyy-mm-dd", "period")
  const problem = seriesDueProblem(s, t, c.period)
  if (problem !== null || !t || t.owner?._tag !== "agent") invalid(problem ?? "not due", "itemId")
  const agent = agentOf(s, t.owner.agentId)
  if (!agent) invalid("the series' agent is gone", "itemId")
  // the day of the schedule time it came due (as a monitor's fire time), so both count against one timed:<agent>:<day>
  const at = c.at ?? tx.now
  if (c.at !== undefined && periodOf(c.at, t.recurrence?.schedule.tz ?? "UTC") !== c.period)
    invalid("at is not a time in that period", "at")
  const day = projectDay(s, at)
  const held = timedCapReached(s, agent, day)
  tx.emit({
    _tag: "SeriesDueNoted",
    itemId: t.id,
    period: c.period,
    day,
    ...(held ? { held: true as const } : {}),
  })
  if (held)
    holdTimed(tx, {
      proposalId: deriveId<Ids["ProposalId"]>("prop", tx.now, `timed:${agent.id}:${day}`),
      agent,
      reason: { _tag: "series_due", itemId: t.id, period: c.period },
      cause: {
        log: { _tag: "project", projectId: s.project?.id as Ids["ProjectId"] },
        seq: tx.state.seq as Ids["Seq"],
      },
      day,
    })
}

/**
 * Decision 0135: system:watchdog, a live worker or validator has produced nothing for `idleMs` (its heartbeats say so)
 * while running. Once per quiet stretch (no output since the last note). Reactions wake its agent (else the lead), who
 * decides to wait, steer or stop. Never stops or creates anything.
 */
export const noteRunQuiet = (tx: Tx, c: Cmd<"NoteRunQuiet">): void => {
  const run = runOf(tx.state, c.runId) ?? invalid("no such run", "runId")
  if (run.kind !== "worker" && run.kind !== "validator")
    invalid("only workers and validators are watched", "runId")
  if (isRunEnded(run) || run.state._tag !== "leased" || run.state.phase !== "running")
    invalid("the run is not running", "runId")
  if (c.idleMs < QUIET_RUN_MS) invalid("not quiet long enough", "idleMs")
  if (run.quietAt !== undefined && c.at - c.idleMs <= run.quietAt)
    invalid("already noted in this quiet stretch", "runId")
  tx.emit({ _tag: "RunQuietNoted", runId: c.runId, idleMs: c.idleMs, at: c.at })
}
